/**
 * Lightweight fuzzy search utility.
 * Normalizes strings, handles diacritics, and scores matches.
 */

/**
 * The form everything typed is compared in: lower case, no diacritics.
 *
 * Somebody looking for "Présidence" types "pre", and somebody with the
 * accented keyboard types "Pré" looking for a record spelled "Presidence".
 * Both have to work, so every place that filters on typed text folds both
 * sides with this. It mirrors `foldText` in the backend's `common/text-fold`.
 */
export function foldText(value: string | null | undefined): string {
  if (!value) return '';
  return value
    .toLowerCase()
    .replace(/ß/g, 'ss')
    .replace(/æ/g, 'ae')
    .replace(/œ/g, 'oe')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    // Letters that carry no separate mark to strip, so NFD leaves them whole
    .replace(/[đð]/g, 'd')
    .replace(/[łŀ]/g, 'l')
    .replace(/ø/g, 'o')
    .replace(/ħ/g, 'h')
    .replace(/ŧ/g, 't')
    .replace(/ı/g, 'i')
    .trim();
}

const normalize = foldText;

function fuzzyScore(query: string, target: string): number {
  const q = normalize(query);
  const t = normalize(target);

  if (!q) return 1;
  if (!t) return 0;

  // Exact match
  if (t === q) return 100;

  // Starts with
  if (t.startsWith(q)) return 80;

  // Contains as substring
  if (t.includes(q)) return 60;

  // Word-start match (query matches the start of any word in target)
  const words = t.split(/\s+/);
  if (words.some(w => w.startsWith(q))) return 70;

  // Fuzzy character matching: all query chars must appear in order in target
  let qi = 0;
  let consecutiveBonus = 0;
  let score = 0;
  let lastMatchIndex = -2;

  for (let ti = 0; ti < t.length && qi < q.length; ti++) {
    if (t[ti] === q[qi]) {
      score += 10;
      if (ti === lastMatchIndex + 1) {
        consecutiveBonus += 5;
      }
      // Bonus for matching at word boundary
      if (ti === 0 || t[ti - 1] === ' ' || t[ti - 1] === '-' || t[ti - 1] === '_') {
        score += 3;
      }
      lastMatchIndex = ti;
      qi++;
    }
  }

  // All query chars matched?
  if (qi < q.length) return 0;

  // Normalize score (max possible: 10*qLen + 5*(qLen-1) + 3*qLen)
  const maxPossible = q.length * 18;
  const normalizedScore = ((score + consecutiveBonus) / maxPossible) * 40;

  return Math.min(normalizedScore, 40);
}

export interface FuzzyResult<T> {
  item: T;
  score: number;
}

/**
 * Search items using fuzzy matching across specified fields.
 * Returns items sorted by relevance score (highest first).
 * Items with score 0 are excluded.
 */
export function fuzzyFilter<T>(
  items: T[],
  query: string,
  getFields: (item: T) => string[],
  minScore: number = 1
): FuzzyResult<T>[] {
  if (!query.trim()) {
    return items.map(item => ({ item, score: 1 }));
  }

  return items
    .map(item => {
      const fields = getFields(item);
      const bestScore = Math.max(...fields.map(f => fuzzyScore(query, f || '')));
      return { item, score: bestScore };
    })
    .filter(r => r.score >= minScore)
    .sort((a, b) => b.score - a.score);
}

/**
 * Highlight matching portions of text for display.
 * Returns an array of { text, highlight } segments.
 */
export function highlightMatch(
  text: string,
  query: string
): { text: string; highlight: boolean }[] {
  if (!query.trim() || !text) {
    return [{ text, highlight: false }];
  }

  const normalizedQuery = normalize(query);
  const normalizedText = normalize(text);
  const idx = normalizedText.indexOf(normalizedQuery);

  if (idx === -1) {
    return [{ text, highlight: false }];
  }

  const segments: { text: string; highlight: boolean }[] = [];
  if (idx > 0) {
    segments.push({ text: text.slice(0, idx), highlight: false });
  }
  segments.push({ text: text.slice(idx, idx + query.length), highlight: true });
  if (idx + query.length < text.length) {
    segments.push({ text: text.slice(idx + query.length), highlight: false });
  }

  return segments;
}
